/**
 * Pure completeness rules for the upload form, plus the memoized hook that
 * assembles the submit payload from them.
 *
 * Split out of `useUploadForm.ts` so that hook stays wiring only, and so these
 * rules can be read - and complexity-checked - on their own. Mirrors how
 * `rateBands.ts` sits next to `useRateBands.ts`.
 */

import { useMemo } from "react";
import type { LandingRateBand, ReceiptSource } from "@/shared/api";
import type { BbWcRatesState } from "./useBbWcRates";
import type { UploadFormValues } from "./UploadForm.types";

/**
 * Checks the two free-text settings.
 *
 * Pulled out so each message sits next to the condition that produces it.
 */
export function validateSettings(
  landingRate: string,
  lookbackDays: string,
): { rate: number; lookback: number; rateError: string | null; lookbackError: string | null } {
  const rate = Number(landingRate);
  const lookback = Number(lookbackDays);

  return {
    rate,
    lookback,
    rateError:
      landingRate.trim() === "" || Number.isNaN(rate) || rate < 0
        ? "Enter a rate of zero or more."
        : null,
    lookbackError:
      !Number.isInteger(lookback) || lookback < 1
        ? "Enter a whole number of days, at least 1."
        : null,
  };
}

/**
 * Checks one BB WC quoted rate against whether its pair is even switched on.
 *
 * Disabled is never an error, even with the field left blank: a blank rate
 * behind an unticked box is not a mistake, it is the box doing its job.
 *
 * @param enabled Either toggle of the pair - "always" already implies
 *   "fallback" by the time this runs, so the caller can pass either one or
 *   their disjunction.
 * @param value The rate as typed, still a string.
 */
export function validateBbWcRate(
  enabled: boolean,
  value: string,
): { rate: number | null; error: string | null } {
  if (!enabled) return { rate: null, error: null };

  const rate = Number(value);
  if (value.trim() === "" || Number.isNaN(rate)) {
    return { rate: null, error: "Enter the Boot Barn wholesale rate, or untick the box." };
  }
  if (rate <= 0) {
    return { rate: null, error: "A rate has to be more than zero." };
  }

  return { rate, error: null };
}

/**
 * Assembles the submit payload, or null when the form is not ready.
 *
 * One place decides completeness, so `canSubmit` and `submit` cannot disagree -
 * a button enabled by one condition and a handler guarded by another is how a
 * form silently does nothing when clicked.
 */
/** Everything completeness depends on. Named so the memo can take it whole. */
export interface CompletenessParts {
  wcReport: File | null;
  wcSource: ReceiptSource;
  opsMaster: File | null;
  parsedRate: number;
  parsedLookback: number;
  rateError: string | null;
  lookbackError: string | null;
  bands: LandingRateBand[] | null;
  /** The whole Boot Barn wholesale state, rather than its fields flattened here - see {@link useBbWcRates}. */
  bbWc: BbWcRatesState;
}

/**
 * True when anything required is missing or invalid.
 *
 * Split out of {@link completeValues} purely to keep that function's own
 * complexity under the lint budget - the two together are one rule, read as
 * one unit.
 */
function isIncomplete(parts: CompletenessParts): boolean {
  const { bbWc } = parts;
  return (
    !parts.opsMaster ||
    parts.bands === null ||
    bbWc.bbWcBands.bands === null ||
    (parts.wcSource === "upload" && !parts.wcReport) ||
    parts.rateError !== null ||
    parts.lookbackError !== null ||
    bbWc.bbWcLandingRateError !== null ||
    bbWc.bbWcCustomsRateError !== null
  );
}

function completeValues(parts: CompletenessParts): UploadFormValues | null {
  if (isIncomplete(parts)) return null;

  const { wcReport, wcSource, opsMaster, bands, bbWc } = parts;

  return {
    // Dropped rather than carried for a NetSuite run, even if a file happens to
    // be held in state from before the toggle was flipped. Posting one would
    // make the request contradict itself.
    wcReport: wcSource === "upload" ? wcReport : null,
    wcSource,
    opsMaster: opsMaster as File,
    landingRate: parts.parsedRate,
    lookbackDays: parts.parsedLookback,
    landingRateBands: bands as LandingRateBand[],
    bbWcRateFallback: bbWc.bbWcRateFallback,
    bbWcRateAlways: bbWc.bbWcRateAlways,
    bbWcLandingRate: bbWc.bbWcParsedLandingRate,
    bbWcRateBands: bbWc.bbWcBands.bands as LandingRateBand[],
    bbWcCustomsFallback: bbWc.bbWcCustomsFallback,
    bbWcCustomsAlways: bbWc.bbWcCustomsAlways,
    bbWcCustomsRate: bbWc.bbWcParsedCustomsRate,
  };
}

/**
 * The submit payload, memoized, or null while the form is incomplete.
 *
 * Memoized so `submit` keeps a stable identity across renders; a fresh callback
 * every keystroke would re-render the button on every character. Depends on
 * each field individually rather than on `parts` or `bbWc` themselves, both of
 * which are fresh objects every call - depending on either would recompute on
 * every render and defeat the memo entirely.
 */
export function useCompleteValues(parts: CompletenessParts): UploadFormValues | null {
  const { bbWc } = parts;

  return useMemo(
    () => completeValues(parts),
    [
      parts.wcReport,
      parts.wcSource,
      parts.opsMaster,
      parts.parsedRate,
      parts.parsedLookback,
      parts.rateError,
      parts.lookbackError,
      parts.bands,
      bbWc.bbWcRateFallback,
      bbWc.bbWcRateAlways,
      bbWc.bbWcParsedLandingRate,
      bbWc.bbWcLandingRateError,
      bbWc.bbWcBands.bands,
      bbWc.bbWcCustomsFallback,
      bbWc.bbWcCustomsAlways,
      bbWc.bbWcParsedCustomsRate,
      bbWc.bbWcCustomsRateError,
    ],
  );
}
